/**
 * What a flow rule's State means, and whether clearing it can do anything.
 *
 * Straight from the SRC manual (AquaLogic 500 & GO Controller UI v230
 * §5.9.2.3). The important thing the state list hides: `State` is NOT an error
 * log. It is the rule's current state, recomputed continuously — "System
 * continuously monitors the flow rule and triggers actions" (§5.9.2) — so
 * clearing only removes the current value. If the cause is still there, the
 * monitor raises it again after the rule's error delay.
 *
 * That is why the manual describes reset as being for errors "that you have
 * acknowledged or fixed" (§3.3.6). Clearing is not fixing.
 *
 * Copy here is hardcoded English, like the rest of the flow rule panel's
 * (see FlowRulePanel/constants.ts) — it needs Tolgee keys.
 */

export type FlowRuleStateKind =
  /** Normal. */
  | 'ok'
  /** Not an error: the rule is deliberately measuring. Ends on its own. */
  | 'mode'
  /** A live alarm, re-evaluated every cycle. */
  | 'alarm'
  /** A configuration fault. Clearing can never help. */
  | 'config'

export interface FlowRuleStateMeta {
  kind: FlowRuleStateKind
  /** Tolgee key for the one-line explanation shown on hover. */
  descriptionKey: string
  /** Whether the Clear control is offered at all. */
  clearable: boolean
}

const STATES: Record<string, FlowRuleStateMeta> = {
  Ok: {
    kind: 'ok',
    descriptionKey: 'flowRuleStateOkDesc',
    clearable: false,
  },
  'No Flow': {
    kind: 'alarm',
    descriptionKey: 'flowRuleStateNoFlowDesc',
    clearable: true,
  },
  'Unexpected Flow': {
    kind: 'alarm',
    descriptionKey: 'flowRuleStateUnexpectedFlowDesc',
    clearable: true,
  },
  'Flow Too Low': {
    kind: 'alarm',
    descriptionKey: 'flowRuleStateFlowTooLowDesc',
    clearable: true,
  },
  'Flow Too High': {
    kind: 'alarm',
    descriptionKey: 'flowRuleStateFlowTooHighDesc',
    clearable: true,
  },
  'Resource Missing': {
    kind: 'config',
    descriptionKey: 'flowRuleStateResourceMissingDesc',
    clearable: false,
  },
  'Verify Flow': {
    kind: 'mode',
    descriptionKey: 'flowRuleStateVerifyFlowDesc',
    clearable: false,
  },
  'Learn Flow': {
    kind: 'mode',
    descriptionKey: 'flowRuleStateLearnFlowDesc',
    clearable: false,
  },
}

/**
 * An unrecognised state is treated as an alarm that can be cleared: a new SRC
 * state is far more likely to be a fault than a mode, and offering the control
 * is recoverable where hiding it is not.
 */
const UNKNOWN: FlowRuleStateMeta = {
  kind: 'alarm',
  descriptionKey: '',
  clearable: true,
}

export const flowRuleStateMeta = (status?: string | null): FlowRuleStateMeta =>
  STATES[String(status ?? '').trim()] ?? UNKNOWN

/** True while the rule is doing something the user should not be alarmed by. */
export const isRuleBusy = (status?: string | null) =>
  flowRuleStateMeta(status).kind === 'mode'

/** Tolgee keys for the clear lifecycle. */
export const CLEAR_COPY = {
  clear: 'flowRuleClearError',
  clearing: 'flowRuleClearingErrorWait',
  returned: 'flowRuleErrorReturned',
  checkIo: 'flowRuleCheckIo',
}

/**
 * Per flow rule: the ids of its valves — a row clears its errors through
 * them, as there is no per-pipe reset — and whether any of them is faulted,
 * so the row's chip can say so without opening the panel.
 */
export const indexValvesByRule = (
  valves: {
    id: number
    flowRuleId?: unknown
    errorState?: unknown
    errorCode?: unknown
  }[],
) => {
  const valveIdsByRuleId = new Map<number, number[]>()
  const rulesWithValveError = new Set<number>()
  valves.forEach((valve) => {
    const ruleId = Number(valve.flowRuleId)
    if (!ruleId) return
    valveIdsByRuleId.set(ruleId, [...(valveIdsByRuleId.get(ruleId) ?? []), valve.id])
    if (Number(valve.errorState ?? 0) !== 0 || Number(valve.errorCode ?? 0) !== 0) {
      rulesWithValveError.add(ruleId)
    }
  })
  return { valveIdsByRuleId, rulesWithValveError }
}
